import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  TextField, Grid, Stack, Switch, FormControlLabel, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, Typography, IconButton
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { createBlog } from '../../api/endpoints/blog';
import { getCategories } from '../../api/endpoints/categories';
import { uploadFile } from '../../api/endpoints/media';
import RichTextEditor from '../../components/RichTextEditor';

const initialForm = {
  title: '', desc: '', content: '', category: '', isActive: true,
};

export default function AddBlogModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setImageFile(null);
      setImagePreview(null);
      fetchCategories();
    }
  }, [open]);

  const fetchCategories = async () => {
    try {
      const res = await getCategories({ type: 'blog' });
      setCategories(res.data.data || []);
    } catch { /* ignore */ }
  };

  const handleChange = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!form.title) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      let imageUrl = '';
      if (imageFile) {
        const uploadRes = await uploadFile(imageFile);
        imageUrl = uploadRes.data.data?.url || '';
      }
      const payload = {
        title: form.title,
        desc: form.desc,
        content: form.content,
        category: form.category || undefined,
        isActive: form.isActive,
      };
      if (imageUrl) payload.image = imageUrl;
      await createBlog(payload);
      toast.success('Blog created');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create blog');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="body1" fontWeight={700} fontSize="1.25rem">Add Blog Post</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label="Title" value={form.title} onChange={handleChange('title')} fullWidth required size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Short Description" value={form.desc} onChange={handleChange('desc')} fullWidth multiline rows={2} size="small" />
          </Grid>
          <Grid item xs={12}>
            <RichTextEditor
              value={form.content}
              onChange={(html) => setForm((p) => ({ ...p, content: html }))}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select value={form.category} label="Category" onChange={handleChange('category')}>
                <MenuItem value="">None</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />} label="Active" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Featured Image</Typography>
            <Button variant="outlined" component="label" startIcon={<AddPhotoAlternateIcon />}>
              {imageFile ? 'Change Image' : 'Upload Image'}
              <input type="file" hidden accept="image/*" onChange={handleImageSelect} />
            </Button>
            {imagePreview && (
              <Box sx={{ mt: 1, position: 'relative', display: 'inline-block' }}>
                <img src={imagePreview} alt="Preview" style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8 }} />
                <IconButton size="small" sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white' }} onClick={() => { setImageFile(null); setImagePreview(null); }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Create Blog'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
